// Recorded drum kit and basses (CC0 libraries, see
// static_content/audio/backing-track/README.md), loaded when the backing track
// is first opened. Same interface as the synth voices in synth.js, which stay
// as the fallback while samples load or if they can't be fetched.

const DRUMS = {
    kick: { layers: 3, roundRobin: 2, level: 1 },
    snare: { layers: 3, roundRobin: 2, level: 0.8 },
    rim: { layers: 2, roundRobin: 2, level: 0.75 },
    hat: { layers: 3, roundRobin: 2, level: 0.45 },
    open: { layers: 2, roundRobin: 1, level: 0.35 },
};
const SHAKER_STROKES = 4;
const SHAKER_LEVEL = 0.4;
const BASS_NOTES = [33, 36, 39, 42, 45, 48, 51, 54, 57];

export const BASS_SOUNDS = ['electric', 'upright', 'synth'];

// Bump when the sample files change, so browsers and the CDN don't serve old ones.
const SAMPLES_VERSION = 2;

function baseUrl() {
    return (window.AUDIO && window.AUDIO.backingTrack) || '/static/audio/backing-track/';
}

// Where the sound starts: skips encoder padding and any silence before the hit.
export function onset(buffer) {
    const data = buffer.getChannelData(0);
    let peak = 0;
    for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
    const threshold = peak * 0.02;
    for (let i = 0; i < data.length; i++) {
        if (Math.abs(data[i]) > threshold) return Math.max(0, i / buffer.sampleRate - 0.001);
    }
    return 0;
}

export class SampleBank {
    constructor(ctx) {
        this.ctx = ctx;
        this.loads = {};
    }

    fetchBuffer(file) {
        return fetch(`${baseUrl()}${file}?v=${SAMPLES_VERSION}`)
            .then(response => {
                if (!response.ok) throw new Error(`${response.status} ${file}`);
                return response.arrayBuffer();
            })
            .then(data => new Promise((resolve, reject) => this.ctx.decodeAudioData(data, resolve, reject)))
            .then(buffer => ({ buffer, offset: onset(buffer) }));
    }

    // Resolves to a playable instrument; cached, so it's safe to call repeatedly.
    load(name) {
        if (!this.loads[name]) {
            this.loads[name] = (name === 'drums' ? this.loadDrums() : this.loadBass(name)).catch(error => {
                delete this.loads[name];
                throw error;
            });
        }
        return this.loads[name];
    }

    async loadDrums() {
        const jobs = [];
        const kit = {};
        Object.entries(DRUMS).forEach(([voice, { layers, roundRobin }]) => {
            kit[voice] = Array.from({ length: layers }, () => []);
            for (let v = 1; v <= layers; v++) {
                for (let r = 1; r <= roundRobin; r++) {
                    jobs.push(this.fetchBuffer(`drums/${voice}_v${v}_r${r}.m4a`).then(s => { kit[voice][v - 1][r - 1] = s; }));
                }
            }
        });
        kit.shaker = [];
        for (let i = 1; i <= SHAKER_STROKES; i++) {
            jobs.push(this.fetchBuffer(`drums/shaker_${i}.m4a`).then(s => { kit.shaker[i - 1] = s; }));
        }
        await Promise.all(jobs);
        return kit;
    }

    async loadBass(name) {
        const notes = await Promise.all(BASS_NOTES.map(midi => this.fetchBuffer(`bass-${name}/${midi}.m4a`).then(s => ({ ...s, midi }))));
        return notes;
    }
}

function play(ctx, sample, output, time, gain, rate = 1) {
    const source = ctx.createBufferSource();
    source.buffer = sample.buffer;
    source.playbackRate.value = rate;
    const amp = ctx.createGain();
    amp.gain.value = gain;
    source.connect(amp).connect(output);
    source.start(time, sample.offset);
    return { source, amp };
}

export class SampledKit {
    constructor(ctx, output, kit) {
        this.ctx = ctx;
        this.output = output;
        this.kit = kit;
        this.robin = {};
        this.openHat = null;
    }

    pick(voice, velocity) {
        const layers = this.kit[voice];
        // Soft ('o', 0.35) → quietest layer, accent ('X', 1) → loudest.
        const index = Math.round(Math.max(0, velocity - 0.35) / 0.65 * (layers.length - 1));
        const layer = layers[Math.min(layers.length - 1, index)];
        this.robin[voice] = ((this.robin[voice] || 0) + 1) % layer.length;
        return layer[this.robin[voice]];
    }

    play(voice, time, velocity) {
        if (voice === 'shaker') {
            this.robin.shaker = ((this.robin.shaker || 0) + 1) % this.kit.shaker.length;
            play(this.ctx, this.kit.shaker[this.robin.shaker], this.output, time, SHAKER_LEVEL * (0.5 + velocity / 2));
            return;
        }
        if (!this.kit[voice]) return;
        if (voice === 'hat' || voice === 'open') this.chokeOpenHat(time);
        const hit = play(this.ctx, this.pick(voice, velocity), this.output, time, DRUMS[voice].level * (0.75 + velocity / 4));
        if (voice === 'open') this.openHat = hit;
    }

    chokeOpenHat(time) {
        if (this.openHat) {
            this.openHat.amp.gain.setTargetAtTime(0, time, 0.02);
            this.openHat = null;
        }
    }
}

const RELEASE = 0.06;

export class SampledBass {
    constructor(ctx, output, notes, level = 0.9) {
        this.ctx = ctx;
        this.output = output;
        this.notes = notes;
        this.level = level;
    }

    nearest(midi) {
        return this.notes.reduce((best, note) => (Math.abs(note.midi - midi) < Math.abs(best.midi - midi) ? note : best));
    }

    note(midi, time, duration, velocity) {
        const sample = this.nearest(midi);
        const { source, amp } = play(this.ctx, sample, this.output, time, this.level * velocity, Math.pow(2, (midi - sample.midi) / 12));
        const end = time + duration;
        amp.gain.setValueAtTime(this.level * velocity, end);
        amp.gain.linearRampToValueAtTime(0, end + RELEASE);
        source.stop(end + RELEASE + 0.01);
        return { source, amp, start: time, end, midi, instrument: this };
    }

    // A recorded note can't slide far without sounding odd, so a late chord
    // change re-plucks the string on the new note instead.
    retune(voice, midi, time) {
        if (time <= voice.start) {
            voice.source.stop(voice.start);
            const fresh = this.note(midi, voice.start, voice.end - voice.start, voice.velocity || 0.8);
            Object.assign(voice, { source: fresh.source, amp: fresh.amp, midi });
            return;
        }
        const end = voice.end;
        this.release(voice, time);
        const fresh = this.note(midi, time, Math.max(0.05, end - time), (voice.velocity || 0.8) * 0.85);
        Object.assign(voice, { source: fresh.source, amp: fresh.amp, start: time, end, midi });
    }

    release(voice, time) {
        if (voice.end <= time) return;
        if (time <= voice.start) {
            voice.source.stop(voice.start);
        } else {
            const gain = voice.amp.gain;
            if (gain.cancelAndHoldAtTime) {
                gain.cancelAndHoldAtTime(time);
            } else {
                gain.cancelScheduledValues(time);
            }
            gain.linearRampToValueAtTime(0, time + 0.03);
            voice.source.stop(time + 0.05);
        }
        voice.end = time;
    }
}
